import React, {useContext, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Grid, InputBase, MenuItem, Select, Stack, Typography, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, FloppyDiskIcon, InformationCircleIcon} from "@hugeicons/core-free-icons";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {useCatalog} from "../../../context/CatalogContext";
import {ProjectRepository} from "../../../repositories/ProjectRepository";
import {filterByOrganization} from "../../../utils/organizationAccess";
import {useRegistryProject} from "../RegistryProjectContext";
import {mirrorToFlows, updateFlow} from "../flowSync";
import {SettingsFooter} from "./SettingsFooter";
import {subProjectLabel} from "../../../utils/subProjects";
import {controlSx, labelSx, selectMenuProps, SectionHeader} from "./settingsStyles";
import useConfirm from "../../../hooks/useConfirm";
import {ProjectRoles} from "../../../context/AppContext";

const toForm = (project) => ({
  name: project.name || "",
  description: project.description || "",
  organizationId: project.organizationId ?? null,
  backendConfigurationId: project.backendConfiguration?.id ?? null,
  type: project.type || "VOICE_ASSISTANT",
});

// the project's current value stays selectable even when it is not in the user's own list
const withCurrent = (items, current) =>
  !current?.id || items.some(item => item.id === current.id) ? items : [...items, current];

// General tab: project information. Languages are configured in their own tab.
export const RegistryGeneralSettings = () => {
  const {project, role, refreshProject} = useRegistryProject();
  const navigate = useNavigate();
  const confirmDelete = useConfirm();
  const [deleting, setDeleting] = useState(false);
  const {organizations} = useContext(SignInContext);
  const {items: myBackendConfigurations} = useCatalog("myBackendConfigurations", {refreshOnMount: true});
  const [form, setForm] = useState(toForm(project));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(toForm(project));
  }, [project]);

  const organizationOptions = withCurrent(
    organizations || [],
    {id: project.organizationId, name: project.organizationName}
  );
  // only public configurations and those of the selected organization can be used
  const allBackendConfigurations = withCurrent(myBackendConfigurations, project.backendConfiguration);
  const isAvailable = (id, organizationId) =>
    filterByOrganization(allBackendConfigurations, organizationId).some(item => item.id === id);
  const backendConfigurations = filterByOrganization(allBackendConfigurations, form.organizationId);

  const hasChanges = JSON.stringify(form) !== JSON.stringify(toForm(project));

  const onChange = (e) => {
    const {name, value} = e.target;
    setForm(prev => ({...prev, [name]: value}));
  };

  const handleOrganizationChange = (e) => {
    const organizationId = e.target.value || null;
    setForm(prev => ({
      ...prev,
      organizationId,
      backendConfigurationId: isAvailable(prev.backendConfigurationId, organizationId) ? prev.backendConfigurationId : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    setLoading(true);
    try {
      const data = {...form, name: form.name.trim()};
      await ProjectRepository.edit(project.id, data);

      const organization = organizations?.find(item => item.id === data.organizationId);
      const subProjects = project.subProjects;
      await mirrorToFlows(subProjects, subProject => updateFlow(subProject, {
        name: subProjects.length > 1
          ? `${data.name} (${subProjectLabel(subProject)})`
          : data.name,
        description: data.description,
        "settings.organization": data.organizationId,
        "settings.organization_type": organization?.organization_type || "",
        "settings.projectType": data.type === "CHATBOT" ? "CHAT_BOT" : "VOICE_BOT",
      }));

      await refreshProject();
      toast.success("Project settings updated");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to update project");
    } finally {
      setLoading(false);
    }
  };

  // project admins only (the API logs the user out on a 403)
  const handleDelete = async () => {
    const confirmed = await confirmDelete({
      title: `Delete "${project.name}"?`,
      message: "The project, its sub projects and its members are deleted. This cannot be undone.",
      confirmText: "Delete",
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      await ProjectRepository.delete(project.id);
      toast.success(`Deleted "${project.name}"`);
      navigate("/projects", {replace: true});
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to delete the project");
      setDeleting(false);
    }
  };

  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Box sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
        <SectionHeader
          icon={<HugeiconsIcon icon={InformationCircleIcon} size={18}/>}
          title={"General Information"}
          description={"The basics - who owns this project and what it is."}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography sx={labelSx}>Project Name</Typography>
            <Box sx={controlSx}>
              <InputBase
                name={"name"}
                value={form.name}
                onChange={onChange}
                fullWidth
                inputProps={{"aria-label": "Project name"}}
                sx={{flex: 1, fontSize: 13, color: "#23264D"}}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={labelSx}>Description</Typography>
            <Box sx={{...controlSx, height: "auto", alignItems: "flex-start", py: 1.25}}>
              <InputBase
                name={"description"}
                value={form.description}
                onChange={onChange}
                fullWidth
                multiline
                rows={3}
                inputProps={{"aria-label": "Description"}}
                sx={{flex: 1, fontSize: 13, color: "#23264D"}}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={labelSx}>Organization</Typography>
            <Select
              variant={"standard"}
              disableUnderline
              fullWidth
              displayEmpty
              value={form.organizationId ?? ""}
              onChange={handleOrganizationChange}
              inputProps={{"aria-label": "Organization"}}
              sx={{
                ...controlSx,
                "& .MuiSelect-select": {py: 0, pr: "22px !important"},
                "& .MuiSelect-icon": {color: "#6B6B80"},
              }}
              MenuProps={selectMenuProps}
            >
              <MenuItem value={""}><em>None</em></MenuItem>
              {organizationOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
            </Select>
            {form.organizationId !== project.organizationId && (
              <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>
                Voice and transcribe models of another organization must be replaced in the Languages tab
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={labelSx}>Backend Configuration</Typography>
            <Select
              variant={"standard"}
              disableUnderline
              fullWidth
              displayEmpty
              value={form.backendConfigurationId ?? ""}
              onChange={e => setForm(prev => ({...prev, backendConfigurationId: e.target.value || null}))}
              inputProps={{"aria-label": "Backend configuration"}}
              sx={{
                ...controlSx,
                "& .MuiSelect-select": {py: 0, pr: "22px !important"},
                "& .MuiSelect-icon": {color: "#6B6B80"},
              }}
              MenuProps={selectMenuProps}
            >
              <MenuItem value={""}><em>None</em></MenuItem>
              {backendConfigurations.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
            </Select>
            <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>
              Public configurations and those of the selected organization
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={labelSx}>Project Type</Typography>
            <ToggleButtonGroup
              exclusive
              value={form.type}
              onChange={(e, value) => value && setForm(prev => ({...prev, type: value}))}
              sx={{
                borderRadius: "10px",
                p: 0.5,
                gap: 0.5,
                bgcolor: "#FAFAFD",
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  border: "none !important",
                  borderRadius: "8px !important",
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#6B6B80",
                },
                "& .Mui-selected": {
                  bgcolor: "rgba(132, 145, 247, 0.14) !important",
                  color: "#4B55C9 !important",
                },
              }}
            >
              <ToggleButton value={"VOICE_ASSISTANT"}>Voice Assistant</ToggleButton>
              <ToggleButton value={"CHATBOT"}>Chatbot Assistant</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>

        {role === ProjectRoles.ADMIN && (
          <Stack
            direction={{xs: "column", sm: "row"}} justifyContent={"space-between"} alignItems={{sm: "center"}} gap={1.5}
            sx={{mt: 3, p: 2, borderRadius: "12px", border: "1px solid #F5C2C0", bgcolor: "#FFF8F7"}}
          >
            <Box>
              <Typography sx={{fontSize: 14, fontWeight: 700, color: "#B42318"}}>Delete Project</Typography>
              <Typography sx={{fontSize: 13, color: "#8A8AA0", mt: 0.25}}>
                Permanently deletes this project with its sub projects and members.
              </Typography>
            </Box>
            <Button
              type={"button"} onClick={handleDelete} disabled={deleting}
              startIcon={deleting ? <CircularProgress size={14} sx={{color: "#B42318"}}/> : <HugeiconsIcon icon={Delete02Icon} size={16}/>}
              sx={{
                flexShrink: 0, textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
                bgcolor: "rgba(180, 35, 24, 0.08)", color: "#B42318", "&:hover": {bgcolor: "rgba(180, 35, 24, 0.14)"},
                "&.Mui-disabled": {bgcolor: "#F0F0F6", color: "#B4B4C3"},
              }}
            >
              Delete Project
            </Button>
          </Stack>
        )}
      </Box>

      <SettingsFooter>
        <Button
          type={"button"} disabled={!hasChanges || loading}
          onClick={() => setForm(toForm(project))}
          sx={{
            textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
            color: "#374151", bgcolor: "transparent", "&:hover": {bgcolor: "#ECECF3"},
            "&.Mui-disabled": {color: "#C9CAD8"},
          }}
        >
          Reset
        </Button>
        <Button
          type={"submit"} disabled={!hasChanges || loading}
          startIcon={loading ? <CircularProgress size={14} sx={{color: "#4B55C9"}}/> : <HugeiconsIcon icon={FloppyDiskIcon} size={16}/>}
          sx={{
            textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
            bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
            "&.Mui-disabled": {bgcolor: "#F0F0F6", color: "#B4B4C3"},
          }}
        >
          Save Changes
        </Button>
      </SettingsFooter>
    </Box>
  );
};
